import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { PageGallery } from "@/components/PageGallery";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import type { NaatiProvinceLanding } from "@/content/naati-corpus/provinces";
import { NAATI_FAQ_CATEGORIES } from "@/content/naati-corpus/faq-defs";
import { NAATI_PROVINCE_META } from "@/content/naati-corpus/meta.gen";

function localFaqs(p: NaatiProvinceLanding) {
  return [
    {
      q: `แปลเอกสาร NAATI ใน${p.provinceTh} ทำที่ไหน?`,
      a: `ไม่ต้องเดินทางเข้ากรุงเทพฯ — ${p.logisticsTh} คำแปลทุกฉบับลงตราประทับและหมายเลข CPN ของนักแปล NAATI Certified ที่ตรวจสอบสถานะได้บนเว็บไซต์ NAATI`,
    },
    {
      q: `ค่าแปล NAATI ใน${p.provinceTh} คิดอย่างไร?`,
      a: `คิดตามจำนวนหน้าและความซับซ้อน เอกสารทะเบียนราษฎรทั่วไปราคาคงที่ต่อฉบับ ส่วนเอกสารกฎหมาย/การเงินคิดตามปริมาณคำ ค่าจัดส่งในจังหวัด${p.provinceTh}คิดตามจริง · ขอใบเสนอราคาฟรีก่อนเริ่มงานทุกครั้ง`,
    },
    {
      q: `ใช้เวลานานเท่าไหร่เมื่ออยู่${p.provinceTh}?`,
      a: p.turnaroundTh,
    },
    {
      q: `เอกสารอะไรที่คน${p.provinceTh}ขอแปล NAATI บ่อยที่สุด?`,
      a: p.topDocsTh.join(" · "),
    },
    {
      q: `คำแปล NAATI จาก${p.provinceTh}ใช้ยื่น DHA ออนไลน์ได้เลยไหม?`,
      a: "ได้ — ไฟล์ PDF ที่มีตราประทับและลายเซ็นนักแปล NAATI อัปโหลดเข้า ImmiAccount ได้โดยตรง และใช้กับ Immigration NZ, IRCC แคนาดา และ UK Home Office ได้เช่นกัน",
    },
    {
      q: `ต้องส่งเอกสารต้นฉบับไปด้วยหรือไม่?`,
      a: "การแปลใช้สแกนสีความละเอียดสูงได้ แต่ควรเก็บต้นฉบับไว้เพราะเจ้าหน้าที่อาจขอตรวจระหว่างพิจารณา หากต้องทำนิติกรณ์ MFA ควบคู่ จึงจำเป็นต้องส่งต้นฉบับ",
    },
  ];
}

export const Route = createFileRoute("/naati/province/$province")({
  loader: async ({ params }): Promise<{ p: NaatiProvinceLanding }> => {
    const { getNaatiProvinceLanding } = await import("@/content/naati-corpus/provinces");
    const p = getNaatiProvinceLanding(params.province);
    if (!p) throw notFound();
    return { p };
  },
  component: NaatiProvincePage,
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "ไม่พบจังหวัด" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { p } = loaderData;
    const title = `แปลเอกสาร NAATI ${p.provinceTh} 2026 — นักแปลรับรอง CPN ยื่นวีซ่าออสเตรเลีย`;
    const description = `บริการแปลเอกสารรับรอง NAATI ใน${p.provinceTh} สำหรับวีซ่าออสเตรเลีย/นิวซีแลนด์ — ${p.turnaroundTh}`;
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: `/naati/province/${params.province}`,
        type: "article",
        alternates: [{ lang: "en", path: `/en/naati/province/${params.province}` }],
        keywords: [
          `แปลเอกสาร naati ${p.provinceTh}`,
          `naati ${p.provinceTh}`,
          `นักแปล naati ${p.provinceTh}`,
          `แปลทะเบียนสมรส naati ${p.provinceTh}`,
          `แปลเอกสารวีซ่าออสเตรเลีย ${p.provinceTh}`,
        ],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "NAATI Translation", url: `${site}/naati` },
              { name: `NAATI ${p.provinceTh}`, url: `${site}/naati/province/${params.province}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(localFaqs(p))) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/naati/province/${params.province}`)) },
      ],
    };
  },
});

function NaatiProvincePage() {
  const { p } = Route.useLoaderData();
  const faqs = localFaqs(p);
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span>{" "}
          <a href="/naati" className="hover:underline">NAATI Translation</a> <span>/</span>{" "}
          <span>{p.provinceTh}</span>
        </nav>

        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          แปลเอกสาร NAATI {p.provinceTh} — นักแปลรับรอง CPN สำหรับวีซ่าออสเตรเลีย (2026)
        </h1>
        <p className="mb-6 text-lg text-muted-foreground">{p.demandTh}</p>

        <div className="mb-8 flex flex-wrap gap-2 text-sm">
          <a href="/contact" className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground">ขอใบเสนอราคาฟรี →</a>
          <a href="/naati/faq" className="rounded-md border px-4 py-2 font-medium hover:bg-muted">คำถามที่พบบ่อย NAATI</a>
          <a href={`/en/naati/province/${p.slug}`} className="rounded-md border px-4 py-2 text-muted-foreground hover:bg-muted">English →</a>
        </div>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">เอกสารที่คน{p.provinceTh}ขอแปล NAATI บ่อยที่สุด</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {p.topDocsTh.map((d: string, i: number) => (
              <li key={i}>{d}</li>
            ))}
          </ul>
        </section>

        <section className="mb-10 rounded-lg border bg-card p-6">
          <h2 className="mb-2 text-xl font-bold">รับ-ส่งเอกสารอย่างไรเมื่ออยู่{p.provinceTh}</h2>
          <p className="mb-3 text-sm leading-relaxed text-muted-foreground">{p.logisticsTh}</p>
          <p className="text-sm leading-relaxed text-muted-foreground"><strong>ระยะเวลา:</strong> {p.turnaroundTh}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">คำถามที่พบบ่อย — NAATI {p.provinceTh}</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="rounded-lg border bg-card p-4" {...(i < 3 ? { open: true } : {})}>
                <summary className="cursor-pointer font-semibold text-sm">{f.q}</summary>
                <p className="faq-answer mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">อ่านต่อตามหมวดคำถาม NAATI</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {NAATI_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/naati/faq/${c.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {c.titleTh}
              </a>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">บริการ NAATI จังหวัดอื่น</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {NAATI_PROVINCE_META.filter((x) => x.slug !== p.slug).map((x) => (
              <a key={x.slug} href={`/naati/province/${x.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                NAATI {x.provinceTh}
              </a>
            ))}
          </div>
        </section>

        <PageGallery category="naati" seed="naati-province" locale="th" />
        <FaqSourceNote topic="translation" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}
